/*
	ai_move.ts
  Copyright (c) 2013-present Human Cube Inc.

  Does force movement turn generation, but not transport loading/unloading.
*/

import BF from '../bfcore/bfconst1';
import bfH from '../bfcore/bf_helper';
import { getNewZoneXY } from '../bfcore/bf_hitmap';
import { Game, PlayerMoves } from '../bfcore/gameTypes';
import { WorkingData, WorkingForce } from '../bfworking/workingTypes';


function findDesiredDestination (working: WorkingData, game: Game, force: WorkingForce): number {
  // force is assumed to not to be cargo or destroyed.
  // Returns ZoneID to move to, or 0 for none (do not move).

  const playerIndex = working.localPlayerIndex;
  const forceTypeIndex = force.ft;
  const forceFlags = game.forceFlags[forceTypeIndex];
  const zoneID = force.z;
  const { friend, foe } = working.ai.data!;

  if( bfH.isLand(game, zoneID) && game.zoneOwner[zoneID] !== playerIndex) {
    // TODO: need to check for allies here and do not stay if friendly.
    return 0;  // In a foe's zone, stay there.
  }

  if (foe[zoneID] > 0) { // Check if should stay put and defend and not move.
    return 0;  // Do not move, stay and defend.
  }
  if (friend[zoneID] <= 1) {
    return 0;  // Keep at least 1 force on each owned zone.
  }
  if (bfH.isCapital(game, zoneID) && friend[zoneID] <= 2) {
    return 0;  // Keep at least 2 forces at a capital.
  }

  // TODO: could weigh protecting factory locations more.
  // TODO: could weigh attacking factory locations more.
  // TODO: above could more intelligently decide to move tanks and defend with troops, etc.

  if (forceFlags & BF.FORCE_FLAG_CAN_MOVE_ON_LAND) {
    let moveToZoneID = 0;

    // Defend! Move to owned zones that have foes attacking, boost for income:
    let value = -1;
    for (let i = 0; i < game.bordersLand[zoneID].length; i++) {
      const tryZoneID = game.bordersLand[zoneID][i];
      if (game.zoneOwner[tryZoneID] === playerIndex) {
        if (foe[tryZoneID] * 4 > friend[tryZoneID]) {  // Ensure at least 4 defenders per attacker.
          const tryValue = foe[tryZoneID] + (game.zoneIncome[tryZoneID] * 3);
          if (value < tryValue) {
            moveToZoneID = tryZoneID;
            value = tryValue;
          }
        }
      }
    }
    if (moveToZoneID) {
      return moveToZoneID;
    }

    // Attack! Try to move to non-owned with least defenders and more income.
    // Boost for attackers already there.
    value = -1;
    for (let i = 0; i < game.bordersLand[zoneID].length; i++) {
      const tryZoneID = game.bordersLand[zoneID][i];
      if (game.zoneOwner[tryZoneID] !== playerIndex) {
        let tryValue = (1000000 - foe[tryZoneID]) + game.zoneIncome[tryZoneID] * 2;
        if (friend[tryZoneID] > 0) {  // Boost if already in this battle.
          tryValue += 1000000;
        }
        if (value < tryValue) {
          moveToZoneID = tryZoneID;
          value = tryValue;
        }
      }
    }
    if(moveToZoneID) {
      return moveToZoneID;
    }

    // Then just try a random land move:
    moveToZoneID = bfH.randomLandNeighbour(game, zoneID);
    if (moveToZoneID) {
      return moveToZoneID;
    }
  }

  if (forceFlags & BF.FORCE_FLAG_CAN_MOVE_ON_WATER) {
    const moveToZoneID = bfH.randomWaterNeighbour(game, zoneID);
    if (moveToZoneID) {
      return moveToZoneID;
    }
  }

  return 0;  // no destination found.
}


export function doMoves (move: PlayerMoves, working: WorkingData, game: Game): void {
  // Does force movements, but not transport loading/unloading.
  // Adds moves to the move parameter.
  // CAREFUL NOT TO MODIFY VALUES IN game, AS IT WILL CORRUPT IT (It might be the source, not a copy).
  // working is cloned and can be modified if desired.

  // move: {
  //        forceID1: [
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1,
  //             zoneID to move to 2, x to move to 2, y to move to 2, extra 2,
  //             zoneID to move to 3, x to move to 3, y to move to 3, extra 3],
  //        forceID2: [ 
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1],
  //        forceID3: [
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1,
  //             zoneID to move to 2, x to move to 2, y to move to 2, extra 2],
  //         ...
  //       },
  //   WHERE:  extra is 0 for normal movement
  //           extra is -1 for load cargo (zoneID is the force ID of cargo to load)
  //           extra is forceID of cargo to unload (zoneID, x, y is for destination of cargo)

  // Generates movement for a player (specified by working.localPlayerIndex).
  // Returns an dictionary of force moves.

  const moveStage = working.ai.moveStage;
  if (moveStage < 0) {
    return;  // AI has done all the move stages, so do nothing.
  }

  if(!working.ai.data) {
    return;  // working.ai.data must be filled.
  }
  
  const { foe } = working.ai.data;

  const playerIndex = working.localPlayerIndex;

  const forcesCount = working.force.length;
  const forces = working.force;

  const capitalZoneID: number = game.capital[playerIndex];
  const recaptureCapital = (capitalZoneID > 0 && game.zoneOwner[capitalZoneID] !== playerIndex);

  // Potentially move all forces that have not moved for this movement stage and are able:
  for (let j = 0; j < forcesCount; j++) {
    let force = forces[j];
    if(force.owner !== playerIndex) {
      continue;  // Not this players force.
    }
    if(force.move.length !== moveStage) {
      // Force has already moved in this stage or has not moved in the previous move stage.
      // So do not move it any more.
      continue;
    }

    const forceID = force.id;
    const forceTypeIndex = force.ft;
    const forceFlags = game.forceFlags[forceTypeIndex];
    const forceFullRange = game.forceRange[forceTypeIndex];
    const zoneID = force.z;

    if( zoneID <= 0 || zoneID > game.zones ) {
      // It is either cargo or destroyed, so do not gererate a move for it.
      continue;
    }

    if (force.r < 1) {
      continue;
    }

    let moveToZoneID = 0;

    if (recaptureCapital) {
      // RED ALERT: Player has lost capital. Move all forces to it or towards it, use any range!
      if (zoneID === capitalZoneID) {
        continue;  // Already at capital, stay there.
      }

      let value = -1;
      if (forceFlags & BF.FORCE_FLAG_CAN_MOVE_ON_LAND) {
        for (let i = 0; i < game.bordersLand[zoneID].length; i++) {
          const tryZoneID = game.bordersLand[zoneID][i];
          let tryValue = working.distance[tryZoneID][capitalZoneID] * 3;
          if (game.zoneOwner[tryZoneID] && game.zoneOwner[tryZoneID] !== playerIndex) {
            tryValue += 1;  // Penalty for going through not owned and non-neutral zones.
          }
          if (foe[tryZoneID] !== 0) {
            tryValue += 1;  // Penalty for obstructed zones.
          }
          if (value === -1 || value > tryValue) {
            moveToZoneID = tryZoneID;
            value = tryValue;
          }
        }
      }
      if (forceFlags & BF.FORCE_FLAG_CAN_MOVE_ON_WATER) {
        for (let i = 0; i < game.bordersWater[zoneID].length; i++) {
          const tryZoneID = game.bordersWater[zoneID][i];
          // TODO: Fine for aircraft, but may not work well for ships as it includes land distance:
          let tryValue = working.distance[tryZoneID][capitalZoneID] * 3;
          if (foe[tryZoneID] !== 0) {
            tryValue += 1;  // Prefer to go through unobstructed zones.
          }
          if (value === -1 || value > tryValue) {
            moveToZoneID = tryZoneID;
            value = tryValue;
          }
        }
      }
    }
    else {
      // Normal movement without panic move to capital.
      if(forceFlags & (BF.FORCE_FLAG_MUST_LAND_FRIENDLY | BF.FORCE_FLAG_MUST_LAND_ANY)) {
        // Make sure aircraft do not move too far from safe landing.
        if (force.r <= Math.ceil(forceFullRange / 2)) {
          // Check for now that they have only moved half their full range.
          // TODO: could do better distance check to safe landing.
          continue;
        }
      }
      moveToZoneID = findDesiredDestination(working, game, force);
    }

    if(moveToZoneID) {
      const pos = getNewZoneXY(game, moveToZoneID);
      if(!move[forceID])
        move[forceID] = [];
      move[forceID].push(moveToZoneID);
      move[forceID].push(pos.x);
      move[forceID].push(pos.y);
      move[forceID].push(0);
      // Also update working data so it is respected properly in this turn generation:
      force.move.push({ z: moveToZoneID, x: pos.x, y: pos.y, e: 0 });
      force.z = moveToZoneID;
      force.x = pos.x;
      force.y = pos.y;
      force.r--;
      working.ai.data.friend[zoneID]--;
      working.ai.data.friend[moveToZoneID]++;
    }
  }
}
